/* eslint-disable jsx-a11y/label-has-associated-control */
import { CostCenter, Product } from '@portal/portal-core';
import * as FjPrimitives from 'components/primitives';
import { AdminClientActions } from 'ducks/admin-client';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { CloudAdminActions } from 'actions';
import { AlertMessage, DatePicker, Input, Modal } from 'components/styled-primitives';
import type { RootState } from 'reducers';
import { useAppDispatch } from 'store/hooks';
import {
  allCurrentSharedComputersSelector,
  currentProductAccessSelector,
  selectAvailableSeats,
} from 'selectors';
import {
  formatHWADisplay,
  formatSharedComputerFingerprint,
  formatSharedComputerHWA,
  pluralize,
} from 'utils';
import { FINGERPRINT_DOCS_URL, getValidationErrors, HWA_DOCS_URL } from '../utils';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.grid.units(0.5)};

  label {
    margin-bottom: 2px;
    color: ${({ theme: { colors } }) => colors.text};
  }
`;

const HardwareAddressInputContainer = styled(InputContainer)`
  margin-top: 0;
`;

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;

  ${InputColumn} + ${InputColumn} {
    margin-left: ${({ theme }) => theme.grid.units(1)};
  }
`;

const InputError = styled(AlertMessage).attrs({
  noIcon: true,
})<{ width?: number }>`
  position: absolute;
  margin-top: 4.2em;
  width: ${({ width, theme }) => (width ? theme.grid.units(width) : '100%')};
`;

const BoxContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.defaultGreyBorderColor};
  padding: ${({ theme }) => theme.grid.units(1)};
  border-radius: ${({ theme }) => theme.borderRadius};
`;

interface FormState {
  label: string;
  hwa: string;
  fingerprint: string;
  endDate: Date | null;
  startDate: Date | null;
  contactLabel: string;
  touchedInputs: Record<string, boolean>;
}

// Minimal shape read from the (loosely-typed) selectAvailableSeats selector.
interface AvailableSeats {
  openSharedSeats: number;
}

const defaultState: FormState = {
  label: '',
  hwa: '',
  fingerprint: '',
  endDate: null,
  startDate: null,
  contactLabel: '',
  touchedInputs: {},
};

const ALL_TOUCHED: Record<string, boolean> = {
  label: true,
  hwa: true,
  fingerprint: true,
  endDate: true,
  startDate: true,
  contactLabel: true,
};

const NewSharedComputerModal = (): React.ReactElement | null => {
  const userAdminState = useSelector((state: RootState) => state.userAdmin);
  // selectAvailableSeats returns a loose union (`{}` when no license matches); cast
  // to the minimal shape actually read here (boundary).
  const seats = useSelector(selectAvailableSeats) as AvailableSeats | undefined;
  const productAccess = useSelector(currentProductAccessSelector);
  const sharedComputerState = useSelector(allCurrentSharedComputersSelector);

  const dispatch = useAppDispatch();
  const hideModal = (): void => {
    dispatch(CloudAdminActions.hideModal());
  };
  const addSharedComputer = (
    costCenterId: string,
    productAccessId: string,
    sharedComputerObj: Record<string, unknown>,
    productName: string,
  ): void => {
    dispatch(
      AdminClientActions.reqUserAdminAddSharedComputer(
        costCenterId,
        productAccessId,
        sharedComputerObj,
        productName,
      ),
    );
  };

  const [form, setForm] = useState<FormState>(defaultState);
  const validationTimeout = useRef<number | undefined>(undefined);

  const validationErrors = () => {
    // FormState stores dates as `Date | null`; getValidationErrors expects
    // `Date | string | undefined`. Convert null -> undefined (both are falsy, so
    // the validation checks behave identically).
    const options = {
      ...form,
      startDate: form.startDate ?? undefined,
      endDate: form.endDate ?? undefined,
    };
    return getValidationErrors(options, sharedComputerState);
  };
  const isValid = (): boolean => !validationErrors().length;

  const handleValueChange = (change: Partial<FormState>): void =>
    setForm((prev) => ({ ...prev, ...change }));

  const handleCloseModal = (): void => {
    setForm(defaultState);
    hideModal();
  };

  // check for validity when the modal opens to indicate required fields, and clear
  // the touch-debounce timeout on unmount (class componentDidMount/componentWillUnmount)
  useEffect(() => {
    if (validationErrors().length) {
      setForm((prev) => ({ ...prev, touchedInputs: ALL_TOUCHED }));
    }
    return () => clearTimeout(validationTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close the modal once a shared computer has been added (faithful translation of the
  // class componentDidUpdate guard, including its original length-vs-array comparison)
  const prevSharedComputerState = useRef(sharedComputerState);
  useEffect(() => {
    // Faithful to the original (always-false) comparison of a length (number) against
    // the array itself; cast preserves the runtime behavior exactly.
    if (prevSharedComputerState.current.length < (sharedComputerState as unknown as number)) {
      handleCloseModal();
    }
    prevSharedComputerState.current = sharedComputerState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedComputerState]);

  const renderErrorByField = (field: string, width?: number): React.ReactElement | null => {
    const error = validationErrors().find(
      (msg: Record<string, string>) => Object.keys(msg)[0] === field,
    );
    if (error && error[field] && form.touchedInputs[field]) {
      return <InputError width={width}>{error[field]}</InputError>;
    }
    return null;
  };

  const handleAdd = (): void => {
    if (!isValid()) {
      setForm((prev) => ({ ...prev, touchedInputs: ALL_TOUCHED }));
      return;
    }
    const { costCenter } = userAdminState;
    const { label, hwa, fingerprint, startDate, endDate, contactLabel } = form;
    const identifiers: Array<{ type: string; value: string }> = [];
    if (hwa) {
      identifiers.push({
        type: CostCenter.SHARED_COMPUTER_IDENTIFIER_TYPES.hwa,
        value: hwa,
      });
    }
    if (fingerprint) {
      identifiers.push({
        type: CostCenter.SHARED_COMPUTER_IDENTIFIER_TYPES.fingerprint,
        value: fingerprint,
      });
    }
    const sharedComputerObj = {
      label,
      identifiers,
      startDate,
      endDate,
      contactLabel,
      status: 'active',
    };
    addSharedComputer(
      (costCenter as { id: string }).id,
      productAccess.id,
      sharedComputerObj,
      productAccess.name,
    );
    handleCloseModal();
  };

  const touchField = (field: string): void => {
    if (!form.touchedInputs[field]) {
      // Timeout keeps this setState() from preventing handleCloseModal()
      validationTimeout.current = window.setTimeout(
        () =>
          setForm((prev) => ({
            ...prev,
            touchedInputs: { ...prev.touchedInputs, [field]: true },
          })),
        100,
      );
    }
  };

  const handleValueChangeAndTouch = (change: Partial<FormState>): void => {
    handleValueChange(change);
    Object.keys(change).forEach(touchField);
  };

  const renderRemainingSeats = (): React.ReactElement | null => {
    if (!seats) {
      return null;
    }
    const { openSharedSeats } = seats;
    // A reduced maxSharedComputers can leave a license over its shared-seat quota
    // (openSharedSeats negative) rather than merely at 0 - "You have -3 open seats available"
    // reads as a bug, so this gets its own message instead of falling through to the
    // positive-count copy.
    if (openSharedSeats < 0) {
      const overQuotaCount = Math.abs(openSharedSeats);
      return (
        <AlertMessage noIcon>
          Your license is currently <strong>{overQuotaCount}</strong>{' '}
          {pluralize(overQuotaCount, 'seat')} over its shared-computer quota. Contact FlowJo sales
          to discuss adding more seats to the license.
        </AlertMessage>
      );
    }
    return (
      <AlertMessage noIcon>
        You have <strong>{openSharedSeats}</strong> open{' '}
        {pluralize(openSharedSeats, 'seat')} available.
      </AlertMessage>
    );
  };

  const { costCenter } = userAdminState;
  if (!costCenter) {
    return null;
    // this next case covers both FlowJo and SeqGeq
  }

  return (
    <Modal
      size={String(FjPrimitives.fjModalSizes.LG)}
      onRequestClose={() => handleCloseModal()}
      title="Add new shared computer"
      cancel={{
        text: 'Cancel',
        callback: () => handleCloseModal(),
      }}
      accept={{
        text: 'Add',
        disabled: !isValid(),
        callback: handleAdd,
      }}
    >
      <span>
        Added Shared Computers will be assigned a seat on your site license.{' '}
        {renderRemainingSeats()}
      </span>
      <br />
      <FormContainer>
        <InputColumn>
          <InputContainer>
            <label>* Name</label>
            <Input
              placeholder="Enter Label"
              onChange={(label: string) => handleValueChange({ label })}
              value={form.label}
              role="button"
              size={14}
              tabIndex={0}
              onBlur={() => touchField('label')}
            />
            {renderErrorByField('label', 14)}
          </InputContainer>
          <br />
          <br />
          <span> * Identifier(s)</span>
          <BoxContainer>
            {(productAccess.name === Product.NAMES.seqGeq ||
              productAccess.name === Product.NAMES.flowJo) && (
              <>
                <InputContainer as={HardwareAddressInputContainer}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label>
                      Hardware Address{' '}
                      <a
                        href={HWA_DOCS_URL}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(HWA_DOCS_URL, '_blank');
                        }}
                      >
                        (help)
                      </a>
                    </label>
                  </div>
                  <Input
                    placeholder="Enter Hardware Address"
                    onChange={(hwa: string) =>
                      handleValueChange({ hwa: formatSharedComputerHWA(hwa) })
                    }
                    value={formatHWADisplay(form.hwa)}
                    role="button"
                    size={String(12)}
                    tabIndex={0}
                    onBlur={() => touchField('hwa')}
                  />
                  {renderErrorByField('hwa', 14)}
                </InputContainer>
                <br />
                <br />
              </>
            )}
            {productAccess.name !== Product.NAMES.seqGeq && (
              <>
                <InputContainer>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label>
                      Fingerprint (lowercase){' '}
                      <a
                        href={FINGERPRINT_DOCS_URL}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(FINGERPRINT_DOCS_URL, '_blank');
                        }}
                      >
                        (help)
                      </a>
                    </label>
                  </div>
                  <Input
                    placeholder="Enter Fingerprint"
                    onChange={(fingerprint: string) =>
                      handleValueChange({
                        fingerprint: formatSharedComputerFingerprint(fingerprint),
                      })
                    }
                    value={form.fingerprint}
                    role="button"
                    size={12}
                    tabIndex={0}
                    onBlur={() => touchField('fingerprint')}
                  />
                  {renderErrorByField('fingerprint', 14)}
                </InputContainer>
              </>
            )}
          </BoxContainer>
        </InputColumn>
        <InputColumn>
          <InputContainer>
            <label>Contact Label</label>
            <Input
              placeholder="Enter Contact Label"
              onChange={(contactLabel: string) => handleValueChange({ contactLabel })}
              value={form.contactLabel}
              role="button"
              size={14}
              tabIndex={0}
              onBlur={() => touchField('contactLabel')}
            />
            {renderErrorByField('contactLabel', 14)}
          </InputContainer>
          <br />
          <br />
          <InputContainer>
            <label>* Activation Date</label>
            <DatePicker
              selected={form.startDate}
              placeholder="Click to select a date"
              onChange={(startDate) => handleValueChangeAndTouch({ startDate })}
              size={String(6.5) as unknown as number}
              // `onBlur` is forwarded by DatePicker to react-datepicker via its rest
              // spread but is absent from the typed DatePickerProps; spread it so
              // assignment is structural-checked.
              {...{ onBlur: () => touchField('startDate') }}
            />
            {renderErrorByField('startDate', 14)}
          </InputContainer>
          <br />
          <br />
          <InputContainer>
            <label>Deactivation Date</label>
            <DatePicker
              selected={form.endDate}
              placeholder="Click to select a date"
              disabled={!form.startDate}
              isClearable
              onChange={(endDate) => {
                if (endDate === null) {
                  setForm((prev) => ({ ...prev, endDate: null }));
                } else {
                  handleValueChangeAndTouch({ endDate });
                }
              }}
              minDate={form.startDate}
              size={String(6.5) as unknown as number}
              time={DatePicker.END_OF_DAY}
              {...{ onBlur: () => touchField('endDate') }}
            />
            {renderErrorByField('endDate')}
          </InputContainer>
        </InputColumn>
      </FormContainer>
    </Modal>
  );
};

export default NewSharedComputerModal;
